// "Experts We Support" directory. Partner cards come from GET /api/partners
// (Airtable); the built-in list below is a fallback.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LEGACY, COLORS, HEADING, TYPE } from 'styles/theme';
import { PAD, WIDTH, SPACE, RADIUS, SURFACE, BUTTON, container } from 'styles/blocks';

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
const RD_BASE = `${LEGACY}/new-york-city-expert-nutritionist-and-registered-dietitians`;

const SPECIALTIES = [
  { label: 'Gut Health & Digestion', href: `${LEGACY}/gut-health-digestion` },
  { label: 'Weight & Metabolic Health', href: `${LEGACY}/weight-metabolic-health` },
  { label: "Women's Health & Hormones", href: `${LEGACY}/womens-health` },
  { label: 'GLP-1 Specialists', href: `${LEGACY}/glp-1-nutrition-experts` },
  { label: 'Clinical & Disease Nutrition', href: `${LEGACY}/clinical-disease-specific-nutrition` },
  { label: 'Mental Health Nutrition', href: `${LEGACY}/mental-health-nutrition` },
  { label: 'Corporate Life Wellness', href: `${LEGACY}/workplace-corporate-wellness` },
  { label: 'Sports & Performance', href: `${LEGACY}/sports-performance-nutrition` },
  { label: 'Eating Disorders', href: `${LEGACY}/eating-disorders-nutritionists-dietitians` },
  { label: 'Fertility / Prenatal', href: `${LEGACY}/directory-fertility-prenatal-postpartum-experts` },
  { label: 'Functional & Integrative Nutrition', href: `${LEGACY}/directory-functional-integrative-nutrition-experts-nyc` },
  { label: 'Pediatric or Family Nutrition', href: `${LEGACY}/find-a-pediatric-nutrition-expert-new-york-city` },
];

// Fallback partner data (used until /api/partners responds, or if it fails).
const FALLBACK = [
  { slug: 'jean-hanks', name: 'Jean Hanks, MS, RDN, CDN', bio: "A Registered Dietitian Nutritionist, native New Yorker, runner and dog mom who alters recipes to make them more nutritious." },
  { slug: 'amy-shapiro', name: 'Amy Shapiro, MS, RN, CDN', bio: "With 15+ years of experience, an authority in nutrition and healthy living, featured in Women's Health, The NY Post, Vogue, Cheddar and NBC." },
  { slug: 'lorraine-kearney', name: 'Lorraine Kearney, CDN, RD, RDN', bio: "Founder and CEO of New York City Nutrition and an internationally published author. Food and mindset impact physical and mental healing." },
  { slug: 'jessica-haskin', name: 'Jessica Haskin, RDN, CDN, IFNCP', bio: "Specializes in IBS, SIBO, leaky gut, histamine intolerance, IBD, gastroparesis, GERD, and autoimmune conditions including Hashimoto's." },
  { slug: 'kendra-bova', name: 'Kendra Bova, MS, RDN, CDN, IFNCP', bio: "An approach rooted in science but deeply personal, looking at stress, sleep, movement, hormones, and daily routines." },
  { slug: 'rachel-dyckman', name: 'Rachel Dyckman, MS, RDN, CDN', bio: "I combine evidence-based nutrition information, motivational techniques, mindset reframing, and counseling to empower you to reach your goals and feel better than ever before." },
  { slug: 'sara-kahn', name: 'Sara Kahn, MS, CNS, CDN', bio: "A board-certified clinical and functional nutritionist specializing in chronic cases of SIBO, IBS and acid reflux." },
  { slug: 'danielle-falchiere', name: 'Danielle Falchiere, RDN, CDN, MS', bio: "Helps clients create sustainable, personalized strategies that strengthen their relationship with food for the long term." },
  { slug: 'gabby-zeagler', name: 'Gabby Zeagler, MS, RDN, CDN, CDCES', bio: "Builds realistic, not restrictive, wellness routines focused on establishing balanced habits." },
  { slug: 'nicki-parlitsis', name: 'Nicki Parlitsis, RD, LDN, CPT', bio: "Personalized, root-cause-driven strategies that fit real life, to help you feel better without burning out or obsessing." },
  { slug: 'lauren-minchen', name: 'Lauren A. Minchen, MPH, RDN, CDN', bio: "Nutrition is unique to each person and should be personalized to support you in achieving your own inspired life." },
  { slug: 'francesca-alfano', name: 'Francesca Alfano, MS, CDN, CNS, IFNCP', bio: "Holds a master's in Nutrition & Integrative Health, is a Certified Nutrition Specialist, and is trained in functional nutrition." },
];

const FAQ = [
  { q: 'When should I take my child to a dietitian for picky eating?', a: "Consider it when picky eating has lasted more than a few months, is causing the child to miss significant nutritional variety, is creating family conflict around mealtimes, or is accompanied by concern about growth." },
  { q: 'Can Square Fare make food for children?', a: "Yes. For children with specific dietary restrictions or clinical needs, the dietitian's specifications guide the meal prep. Square Fare has also worked with NYC daycare settings, with excellent results." },
  { q: "How does Square Fare handle a family where one person has restrictions and others don't?", a: "Each person's meals are customized independently, for example a parent managing gestational diabetes receives carb-calibrated meals while their partner and children get personalized but less restrictive meals, all from one kitchen." },
  { q: 'What age range does Square Fare serve?', a: "Adults and families. For children, meals follow specifications from the child's pediatric dietitian and parents. Contact claire@getsquarefare.com to discuss pediatric and family meal plans." },
];

const initials = (name) => name.split(',')[0].split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('');
const fallbackRows = () => FALLBACK.map(f => ({ slug: f.slug, name: f.name, bio: f.bio, photo: '', profileUrl: `${RD_BASE}/${f.slug}` }));

export default function ExpertsPage() {
  const { dark, text } = COLORS;
  const [partners, setPartners] = useState(fallbackRows);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/partners`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!alive || !data || !Array.isArray(data.partners) || !data.partners.length) return;
        const merged = data.partners.map(p => {
          const fb = FALLBACK.find(f => f.slug === p.slug) || {};
          const name = p.name ? (p.credentials ? `${p.name}, ${p.credentials}` : p.name) : (fb.name || '');
          return {
            slug: p.slug || fb.slug || name,
            name,
            bio: p.bio || fb.bio || '',
            photo: p.photo || '',
            profileUrl: p.profileUrl || (fb.slug ? `${RD_BASE}/${fb.slug}` : '#'),
          };
        });
        setPartners(merged);
      })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, []);

  return (
    <div>
      {/* Find a Specialist */}
      <section style={{ padding: `${SPACE.bandY}px ${PAD}px 48px`, textAlign: 'center' }}>
        <h1 style={{ ...HEADING, fontSize: TYPE.displayHeading, lineHeight: 1.1, margin: '0 0 28px' }}>
          Find a Specialist
        </h1>
        <p style={{ maxWidth: `${WIDTH.measure}px`, margin: '0 auto 44px', fontSize: TYPE.body, lineHeight: 1.85, color: text }}>
          Square Fare supports the work of nutritionists, endocrinologists, fitness trainers, doulas, health
          coaches and more, across the tri-state area. Following the specialists' guidelines for their
          individual patients and clients, we deliver personalized, fresh, clean meals tailored to each one.
          Browse our community of providers by specialty below.
        </p>
        <div style={{ maxWidth: '640px', margin: '0 auto', columnCount: 2, columnGap: '48px' }}>
          {SPECIALTIES.map(s => (
            <a key={s.label} href={s.href} style={{
              display: 'block', padding: '12px 0', color: dark, textDecoration: 'underline',
              fontSize: TYPE.body, textAlign: 'left', breakInside: 'avoid',
            }}>{s.label}</a>
          ))}
        </div>
      </section>

      {/* Nutrition partners — photo + name + bio rows. The founder quote and
          CHERRY code that used to sit above this were removed (SFLANDING-120). */}
      <section style={{ ...container(WIDTH.quote), padding: `${SPACE.blockY}px ${PAD}px` }}>
        <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 44px', textAlign: 'center' }}>Our Nutrition Partners</h2>
        {partners.map(e => (
          <div key={e.slug} style={{ display: 'flex', gap: '48px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '56px' }}>
            <div style={{
              width: '200px', height: '200px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
              backgroundColor: '#eee6d6', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#b3a687', ...HEADING, fontSize: TYPE.sectionHeading,
            }}>
              {e.photo
                ? <img src={e.photo} alt={e.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : initials(e.name)}
            </div>
            <div style={{ flex: '1 1 360px' }}>
              <h3 style={{ ...HEADING, fontSize: TYPE.bandHeading, lineHeight: 1.15, margin: '0 0 16px' }}>{e.name}</h3>
              <p style={{ fontSize: TYPE.body, lineHeight: 1.75, color: text, margin: '0 0 18px', maxWidth: '620px' }}>{e.bio}</p>
              {/* Profile Link in Airtable is now /experts/<slug> (SFLANDING-120);
                  an absolute URL still works for anyone not ported yet. */}
              {e.profileUrl.startsWith('/')
                ? <Link to={e.profileUrl} style={{ color: dark, textDecoration: 'underline', fontWeight: 600, fontSize: TYPE.bodySm }}>Read More</Link>
                : <a href={e.profileUrl} style={{ color: dark, textDecoration: 'underline', fontWeight: 600, fontSize: TYPE.bodySm }}>Read More</a>}
            </div>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section style={{ padding: `20px ${PAD}px ${SPACE.bandTop}px` }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 28px', textAlign: 'center' }}>Frequently Asked Questions</h2>
          {FAQ.map(f => (
            <div key={f.q} style={{ background: SURFACE.cardBg, border: SURFACE.rule, borderRadius: `${RADIUS.tile}px`, padding: '20px 22px', marginBottom: '14px' }}>
              <div style={{ fontWeight: 700, fontSize: TYPE.bodySm, marginBottom: '8px' }}>{f.q}</div>
              <p style={{ fontSize: TYPE.bodySm, lineHeight: 1.65, color: text, margin: 0 }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Become a partner CTA */}
      <section style={{ textAlign: 'center', padding: `20px ${PAD}px ${SPACE.bandY}px` }}>
        <h2 style={{ ...HEADING, fontSize: TYPE.bandHeading, margin: '0 0 16px' }}>Are you a nutrition expert?</h2>
        <Link to="/contact-form-partners" style={BUTTON}>Become a Partner</Link>
      </section>
    </div>
  );
}
